import { FlattenedSurveyAnswers } from "utils/v3/surveyUtils";

export const COLON_CANCER_SCREENING_MIN_AGE = 45;

export const getMemberAgeFromDob = (
  dob: string | undefined | null
): number | null => {
  if (!dob) {
    return null;
  }

  const dateOfBirth = new Date(dob);
  if (Number.isNaN(dateOfBirth.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - dateOfBirth.getFullYear();
  const monthDelta = today.getMonth() - dateOfBirth.getMonth();

  if (
    monthDelta < 0 ||
    (monthDelta === 0 && today.getDate() < dateOfBirth.getDate())
  ) {
    age -= 1;
  }

  return age;
};

export const isMemberEligibleForColonCancerScreeningPrompt = (
  dob: string | undefined | null
): boolean => {
  const age = getMemberAgeFromDob(dob);
  return age !== null && age >= COLON_CANCER_SCREENING_MIN_AGE;
};

export type ColonCancerScreeningAgeBand = "45-54" | "55-plus";

export const getColonCancerScreeningAgeBand = (
  dob: string | undefined | null
): ColonCancerScreeningAgeBand | null => {
  const age = getMemberAgeFromDob(dob);

  if (age === null || age < COLON_CANCER_SCREENING_MIN_AGE) {
    return null;
  }

  if (age <= 54) {
    return "45-54";
  }

  return "55-plus";
};

/** Basal-cell skin cancer does not count as reported cancer. */
export const hasMemberReportedCancer = (
  flattenedSurveyAnswers: FlattenedSurveyAnswers
): boolean => {
  return (
    flattenedSurveyAnswers["active-cancer-diagnosis"] === true ||
    (flattenedSurveyAnswers["have-you-ever-been-told-you-have-cancer"] ===
      true &&
      flattenedSurveyAnswers["cancer-type"] !==
        "basal-cell-or-squamous-cell-skin-cancer") ||
    (flattenedSurveyAnswers["which-cancer-status-best-applies-to-you"] !==
      undefined &&
      flattenedSurveyAnswers["cancer-type"] !==
        "basal-cell-or-squamous-cell-skin-cancer")
  );
};

/**
 * Member finished the colon screening survey on the Cologuard path
 * (has insurance and is due for screening). Asking for a navigator
 * callback does not take them off this path.
 */
export const wasCologuardQualificationSurveyCompletedWithCologuardSuggestion = (
  flattenedSurveyAnswers: FlattenedSurveyAnswers
): boolean => {
  return (
    flattenedSurveyAnswers["active-health-insurance-coverage"] === true &&
    flattenedSurveyAnswers["ever-had-colon-cancer-screen"] !== undefined
  );
};

export const didMemberRequestNavigatorCallback = (
  flattenedSurveyAnswers: FlattenedSurveyAnswers | Record<string, unknown>
): boolean => {
  if (flattenedSurveyAnswers["want-to-speak-with-navigation"] === "yes") {
    return true;
  }

  const phone = flattenedSurveyAnswers["preferred-contact-phone-number"];
  const email = flattenedSurveyAnswers["preferred-contact-email-address"];

  return Boolean(phone) && Boolean(email);
};
